import React, { useMemo } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';

const propTypes = {
    value: PropTypes.string.isRequired,
    column: PropTypes.shape({
        filterValue: PropTypes.string,
    }).isRequired,
    row: PropTypes.shape({
        original: PropTypes.shape({
            branches: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                })
            ),
            id: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};

const defaultProps = {};

const MAX_LENGTH = 40;
const BRANCH_TRUNCATE_LENGTH = 10;

/**
 * Truncates a single branch name to min(first word length, BRANCH_TRUNCATE_LENGTH) chars + '...'
 */
function truncateBranchName(name) {
    const firstSpaceIdx = name.indexOf(' ');
    const firstWord = firstSpaceIdx === -1 ? name : name.slice(0, firstSpaceIdx);
    const truncateAt = Math.min(firstWord.length, BRANCH_TRUNCATE_LENGTH);
    return `${name.slice(0, truncateAt)}...`;
}

function BranchesCell({ value, column: { filterValue }, row: { original } }) {
    const { branches = [] } = original;
    const totalBranchCount = branches.length;

    //#region Display Logic
    const display = useMemo(() => {
        if (filterValue) {
            const lowerFilterValue = R.toLower(filterValue);
            const matchedBranches = R.filter((branch) => {
                return R.toLower(branch.name).includes(lowerFilterValue);
            }, branches);

            const matchedNames = R.map(R.prop('name'), matchedBranches);
            const matchedValue = R.join(', ', matchedNames);

            return (
                <>
                    {' ... '}
                    <strong>{matchedValue}</strong>
                    {' ... '} <span>({totalBranchCount})</span>
                </>
            );
        }

        // No filter: build display by iterating branch names individually.
        // For names that don't fit, show min(first word, 10 chars) + '...' and continue
        // showing subsequent branch names so at least a prefix and next branches are visible.
        // `value` is already the sorted, joined branch names produced by the table accessor.
        // When branches is empty the accessor yields '--', which we preserve as-is.
        const branchNames = branches.length > 0 ? R.split(', ', value) : [];

        let next;
        if (value.length <= MAX_LENGTH) {
            next = value;
        } else {
            const parts = [];
            let currentLength = 0;

            for (let i = 0; i < branchNames.length; i += 1) {
                const name = branchNames[i];
                const separator = parts.length > 0 ? ', ' : '';

                if (currentLength + separator.length + name.length <= MAX_LENGTH) {
                    parts.push(name);
                    currentLength += separator.length + name.length;
                } else {
                    // Name doesn't fit fully — show a meaningful prefix then continue
                    const truncated = truncateBranchName(name);
                    parts.push(truncated);
                    currentLength += separator.length + truncated.length;
                }
            }

            next = parts.join(', ');
        }

        return (
            <>
                {next} <span>({totalBranchCount})</span>
            </>
        );
    }, [filterValue, branches, value, totalBranchCount]);
    //#endregion

    return <span>{display}</span>;
}

BranchesCell.propTypes = propTypes;
BranchesCell.defaultProps = defaultProps;

export default BranchesCell;
